/**
 * Service for handling photo uploads and image processing
 */

import { crashlytics } from "./firebase";

export interface PhotoUploadResult {
  success: boolean;
  imageUrl?: string;
  error?: string;
}

export interface PhotoValidationError {
  type: "SIZE" | "FORMAT" | "DIMENSIONS" | "GENERAL";
  message: string;
}

// Configuration constants
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FORMATS = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_DIMENSION = 2048; // Max width/height in pixels

/**
 * Validates an uploaded photo file
 */
export const validatePhoto = (file: File): PhotoValidationError | null => {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      type: "SIZE",
      message: "Photo must be smaller than 10MB",
    };
  }

  // Check file format
  if (!ALLOWED_FORMATS.includes(file.type)) {
    return {
      type: "FORMAT",
      message: "Please upload a JPEG, PNG, or WebP image",
    };
  }

  return null;
};

/**
 * Validates image dimensions
 */
export const validateImageDimensions = (
  img: HTMLImageElement,
): PhotoValidationError | null => {
  if (img.width > MAX_DIMENSION || img.height > MAX_DIMENSION) {
    return {
      type: "DIMENSIONS",
      message: `Image dimensions must be ${MAX_DIMENSION}px or smaller`,
    };
  }

  return null;
};

/**
 * Resizes an image if it exceeds maximum dimensions
 */
export const resizeImage = (
  file: File,
  maxDimension: number = MAX_DIMENSION,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      let { width, height } = img;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > maxDimension || height > maxDimension) {
        const ratio = Math.min(maxDimension / width, maxDimension / height);
        width *= ratio;
        height *= ratio;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw the resized image
      ctx?.drawImage(img, 0, 0, width, height);

      // Convert to data URL
      const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
      resolve(dataUrl);
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Processes and uploads a photo file
 */
export const processPhoto = async (file: File): Promise<PhotoUploadResult> => {
  try {
    // Validate file
    const validationError = validatePhoto(file);
    if (validationError) {
      return {
        success: false,
        error: validationError.message,
      };
    }

    // Create image element to check dimensions
    const img = new Image();
    const imageUrl = await new Promise<string>((resolve, reject) => {
      img.onload = async () => {
        try {
          // Check dimensions
          const dimensionError = validateImageDimensions(img);

          // If image is too large, resize it
          if (dimensionError) {
            const resizedDataUrl = await resizeImage(file);
            resolve(resizedDataUrl);
          } else {
            // Image is fine, convert to data URL
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
            resolve(dataUrl);
          }
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = URL.createObjectURL(file);
    });

    return {
      success: true,
      imageUrl,
    };
  } catch (error) {
    crashlytics.logError(
      `Error in processPhoto: ${JSON.stringify(error?.message)}`,
      "photoUploadService.ts",
      error,
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to process photo",
    };
  }
};

/**
 * Creates a preview URL for a file
 */
export const createPreviewUrl = (file: File): string => {
  return URL.createObjectURL(file);
};

/**
 * Cleans up a preview URL to prevent memory leaks
 */
export const cleanupPreviewUrl = (url: string): void => {
  if (url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
};

/**
 * Utility to compress image quality
 */
export const compressImage = (
  file: File,
  quality: number = 0.8,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      resolve(dataUrl);
    };

    img.onerror = () => {
      reject(new Error("Failed to compress image"));
    };

    img.src = URL.createObjectURL(file);
  });
};
